import { Link, useRouterState } from "@tanstack/react-router";
import { Home, ShoppingBag, BookOpen, ShoppingCart, User } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useT } from "@/lib/i18n/provider";
import type { TranslationKey } from "@/lib/i18n/dictionary";

type Tab = {
  label: string;
  key: TranslationKey;
  to?: string;
  icon: typeof Home;
  match?: (path: string) => boolean;
  action?: "cart";
};

const tabs: Tab[] = [
  { label: "Home", key: "nav.home", to: "/", icon: Home, match: (p) => p === "/" },
  { label: "Shop", key: "nav.shop", to: "/shop", icon: ShoppingBag, match: (p) => p.startsWith("/shop") || p.startsWith("/products") },
  { label: "Cart", key: "nav.cart", icon: ShoppingCart, action: "cart" },
  { label: "Library", key: "nav.library", to: "/blog", icon: BookOpen, match: (p) => p.startsWith("/blog") || p.startsWith("/ingredients") },
  { label: "Account", key: "nav.account", to: "/account", icon: User, match: (p) => p.startsWith("/account") || p.startsWith("/auth") },
];

export function BottomNav({ onCartClick }: { onCartClick: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const translate = useT();
  const itemCount = useCartStore((s) => s.items.reduce((sum, i) => sum + i.quantity, 0));

  // Product pages own the bottom of the screen with a single compact purchase
  // bar — no competing tab bar there.
  if (pathname.startsWith("/products/")) return null;

  return (
    <nav
      data-bottom-nav
      aria-label={translate("nav.primary")}
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-3 md:hidden"
      style={{ bottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="pointer-events-auto flex w-full max-w-[400px] items-center justify-between rounded-full border border-black/5 bg-white/95 px-2 py-2 shadow-[0_10px_30px_-8px_rgba(43,45,66,0.18),0_2px_8px_rgba(43,45,66,0.06)] backdrop-blur-xl">
        {tabs.map((tab) => {
          const active = tab.match ? tab.match(pathname) : false;
          const Icon = tab.icon;
          const isCart = tab.action === "cart";
          const content = (
            <span
              className={`relative flex flex-col items-center justify-center gap-0.5 rounded-full transition-all duration-300 ${
                active
                  ? "h-12 min-w-12 bg-[#2B2D42] text-white shadow-[0_4px_12px_rgba(43,45,66,0.25)]"
                  : "h-12 min-w-12 text-foreground/55 hover:text-foreground"
              }`}
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
              <span className="text-[10px] font-semibold tracking-[0.02em]">{translate(tab.key)}</span>
              {isCart && itemCount > 0 && (
                <span
                  aria-label={translate("nav.cartItems", { count: itemCount })}
                  className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#2B2D42] px-1 text-[9px] font-bold text-white"
                >
                  {itemCount}
                </span>
              )}
            </span>
          );
          if (tab.action === "cart") {
            return (
              <button
                key={tab.label}
                type="button"
                onClick={onCartClick}
                aria-label={translate("nav.openCart")}
                className="flex-1 flex justify-center"
              >
                {content}
              </button>
            );
          }
          return (
            <Link
              key={tab.label}
              to={tab.to!}
              aria-label={translate(tab.key)}
              aria-current={active ? "page" : undefined}
              className="flex-1 flex justify-center"
            >
              {content}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
